import React, { useCallback, useEffect, useState } from "react";
import { LayoutDashboard, Route as RouteIcon, Map, History, User, Star } from "lucide-react";
import PanelShell from "../../components/PanelShell";
import StatusBadge from "../../components/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { driverApi } from "@/lib/driverApi";

const navItems = [
  { to: "/conductor", label: "Inicio", icon: LayoutDashboard, end: true },
  { to: "/conductor/ruta", label: "Ruta optimizada", icon: RouteIcon },
  { to: "/conductor/rutas-tarifas", label: "Mis rutas", icon: Map },
  { to: "/conductor/historial", label: "Historial", icon: History },
  { to: "/conductor/calificaciones", label: "Calificaciones", icon: Star },
  { to: "/conductor/perfil", label: "Perfil", icon: User },
];
const bottomNav = [
  { to: "/conductor", label: "Inicio", icon: LayoutDashboard, end: true },
  { to: "/conductor/ruta", label: "Ruta", icon: RouteIcon },
  { to: "/conductor/rutas-tarifas", label: "Rutas", icon: Map },
  { to: "/conductor/historial", label: "Viajes", icon: History },
  { to: "/conductor/perfil", label: "Perfil", icon: User },
];

const STATUS_LABELS = { pendiente_pago: "Pendiente", confirmado: "Confirmado", completado: "Completado", cancelado: "Cancelado" };
const NEXT_STATUS = { pendiente_pago: "confirmado", confirmado: "completado" };

const DriverHistory = () => {
  const { user } = useAuth();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("Todos");
  const [error, setError] = useState("");
  const [paymentRefs, setPaymentRefs] = useState({});
  const [reviewingId, setReviewingId] = useState(null);
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: "" });
  const [reviewedIds, setReviewedIds] = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await driverApi.listReservations();
      setReservations(data);
      // has_client_review viene del servidor -- antes reviewedIds solo se
      // llenaba al enviar una reseña en la sesión actual, así que
      // recargar la página (u otra sesión) hacía reaparecer "Calificar
      // cliente" en una reserva ya reseñada, aunque el dato SÍ estaba
      // guardado (bug real: la reseña se guardaba bien, pero la UI no lo
      // reflejaba después de recargar).
      setReviewedIds(new Set(data.filter((r) => r.has_client_review).map((r) => r.id)));
    } catch {
      setError("No se pudieron cargar las reservas.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = tab === "Todos" ? reservations : reservations.filter((r) => r.status === tab);

  const advanceStatus = async (r) => {
    const next = NEXT_STATUS[r.status];
    if (!next) return;
    setError("");
    try {
      await driverApi.updateReservationStatus(r.id, next);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "No se pudo actualizar el estado.");
    }
  };

  const markPaid = async (r) => {
    setError("");
    try {
      await driverApi.markReservationPaid(r.id, paymentRefs[r.id] || "");
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "No se pudo marcar como pagado.");
    }
  };

  const submitReview = async (id) => {
    setError("");
    try {
      await driverApi.createClientReview(id, { rating: Number(reviewForm.rating), comment: reviewForm.comment || null });
      setReviewedIds((prev) => new Set(prev).add(id));
      setReviewingId(null);
      setReviewForm({ rating: 5, comment: "" });
    } catch (err) {
      // 409 = ya existe una reseña para esta reserva (típicamente porque
      // reviewedIds quedó desactualizado -- otra pestaña/sesión la mandó
      // primero). Se autocorrige acá para no dejar el botón "Calificar
      // cliente" ofreciéndose de nuevo hasta el próximo reload manual.
      if (err.response?.status === 409) {
        setReviewedIds((prev) => new Set(prev).add(id));
        setReviewingId(null);
      }
      setError(err.response?.data?.detail || "No se pudo enviar la reseña.");
    }
  };

  return (
    <PanelShell role="Conductor" navItems={navItems} bottomNavItems={bottomNav} userName={user?.full_name} testId="driver-history">
      <div className="max-w-6xl mx-auto px-4 lg:px-8 py-8 lg:py-12">
        <p className="text-[10px] uppercase tracking-brand text-[#C9A227] font-display">Reservas</p>
        <h1 className="font-display font-bold text-3xl uppercase tracking-tight mt-1">Historial y pagos</h1>

        {error && <p className="mt-4 text-xs text-red-400 font-body">{error}</p>}

        <div className="mt-6 flex items-center gap-2 flex-wrap">
          {["Todos", "pendiente_pago", "confirmado", "completado", "cancelado"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 border rounded-full text-[10px] uppercase tracking-wider font-display transition ${
                tab === t ? "bg-[#C9A227] border-[#C9A227] text-[#0D0E10] font-bold" : "border-[#2E2F33] text-[#A0A0A5] hover:border-[#C9A227]/50"
              }`}
              data-testid={`driver-filter-${t}`}
            >
              {t === "Todos" ? t : STATUS_LABELS[t]}
            </button>
          ))}
        </div>

        <div className="mt-6 bg-[#1C1D20] border border-[#2E2F33] rounded-xl overflow-hidden" data-testid="history-table">
          <ul className="divide-y divide-[#2E2F33]">
            {!loading &&
              filtered.map((r) => (
                <li key={r.id} className="px-5 py-4" data-testid={`driver-trip-${r.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-body text-[#F5F5F5]">{r.contact_name}</p>
                      <p className="text-xs text-[#A0A0A5]">
                        {r.modality} · {new Date(r.scheduled_at).toLocaleString("es-PE")}
                        {r.seat_numbers.length > 0 && ` · Asientos ${r.seat_numbers.join(", ")}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <StatusBadge status={STATUS_LABELS[r.status]} />
                      <span className="text-[10px] uppercase tracking-wider text-[#A0A0A5]">
                        Pago: {r.payment_status === "pagado" ? "Pagado" : "Pendiente"} ({r.payment_method})
                      </span>
                      <p className="font-display font-bold text-[#C9A227]">S/ {r.price_total}</p>
                    </div>
                  </div>

                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    {r.payment_status !== "pagado" && r.status !== "cancelado" && (
                      <>
                        <input
                          placeholder="Referencia de pago (opcional)"
                          value={paymentRefs[r.id] || ""}
                          onChange={(e) => setPaymentRefs((p) => ({ ...p, [r.id]: e.target.value }))}
                          className="bg-[#0D0E10] border border-[#2E2F33] rounded-lg px-3 py-1.5 text-xs text-[#F5F5F5] font-body outline-none"
                          data-testid={`payment-ref-${r.id}`}
                        />
                        <button
                          onClick={() => markPaid(r)}
                          className="px-3 py-1.5 border border-[#C9A227] text-[#C9A227] hover:bg-[#C9A227]/10 uppercase tracking-wider font-display text-[10px] rounded-lg"
                          data-testid={`mark-paid-${r.id}`}
                        >
                          Marcar pagado
                        </button>
                      </>
                    )}
                    {NEXT_STATUS[r.status] && (
                      <button
                        onClick={() => advanceStatus(r)}
                        className="px-3 py-1.5 bg-[#C9A227] hover:bg-[#D4AF37] text-[#0D0E10] font-display font-bold uppercase tracking-wider text-[10px] rounded-lg"
                        data-testid={`advance-status-${r.id}`}
                      >
                        Marcar {STATUS_LABELS[NEXT_STATUS[r.status]]}
                      </button>
                    )}
                    {r.status === "completado" && !reviewedIds.has(r.id) && (
                      <button
                        onClick={() => setReviewingId(reviewingId === r.id ? null : r.id)}
                        className="px-3 py-1.5 border border-[#C9A227] text-[#C9A227] hover:bg-[#C9A227]/10 uppercase tracking-wider font-display text-[10px] rounded-lg flex items-center gap-1"
                        data-testid={`review-client-${r.id}`}
                      >
                        <Star size={11} /> Calificar cliente
                      </button>
                    )}
                    {reviewedIds.has(r.id) && (
                      <span className="text-[10px] text-[#C9A227] uppercase tracking-wider font-display">Cliente calificado</span>
                    )}
                  </div>

                  {reviewingId === r.id && (
                    <div className="mt-4 pt-4 border-t border-[#2E2F33]" data-testid={`review-client-form-${r.id}`}>
                      <div className="flex items-center gap-1 mb-3">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <button key={n} type="button" onClick={() => setReviewForm((f) => ({ ...f, rating: n }))} data-testid={`client-star-${n}`}>
                            <Star size={20} className={n <= reviewForm.rating ? "text-[#C9A227]" : "text-[#2E2F33]"} fill={n <= reviewForm.rating ? "currentColor" : "none"} />
                          </button>
                        ))}
                      </div>
                      <textarea
                        rows={2}
                        value={reviewForm.comment}
                        onChange={(e) => setReviewForm((f) => ({ ...f, comment: e.target.value }))}
                        placeholder="¿Cómo fue el pasajero? (opcional)"
                        className="w-full bg-[#0D0E10] border border-[#2E2F33] focus:border-[#C9A227] rounded-lg px-4 py-3 text-sm text-[#F5F5F5] font-body outline-none"
                        data-testid={`review-client-comment-${r.id}`}
                      />
                      <button
                        onClick={() => submitReview(r.id)}
                        className="mt-3 px-5 py-2 bg-[#C9A227] hover:bg-[#D4AF37] text-[#0D0E10] font-display font-bold uppercase tracking-wider text-xs rounded-lg"
                        data-testid={`submit-review-client-${r.id}`}
                      >
                        Enviar reseña
                      </button>
                    </div>
                  )}
                </li>
              ))}
          </ul>
          {!loading && filtered.length === 0 && <p className="px-5 py-8 text-center text-xs text-[#A0A0A5] font-body">No hay reservas en este estado.</p>}
        </div>
      </div>
    </PanelShell>
  );
};

export default DriverHistory;
